import React, {createContext, useContext, useImperativeHandle} from 'react';
import {atom} from 'jotai';
import {
	type IContextChildren,
	type IContextRef,
	renderChildrenOrChildrenFunction,
} from '@netx/core-next';
import {
	useSearchRules,
	type UseSearchRulesParams,
} from '../hooks/useSearchRules';

export type SearchRulesContextType = ReturnType<typeof useSearchRules>;

export const SearchRulesContext = createContext<SearchRulesContextType>({
	searchRulesAtom: atom<any>({}),
	searchRulesValidAtom: atom<any>({}),
	searchSortAtom: atom<any>({}),
	setSearchRules: () => {},
	setSearchSort: () => {},
});

export const useSearchRulesProvider = () => useContext(SearchRulesContext);

export type SearchRulesProviderChildrenProps = SearchRulesContextType;

export type SearchRulesProviderRef = IContextRef<SearchRulesContextType>;

export type SearchRulesProviderProps = UseSearchRulesParams & {
	ref?: SearchRulesProviderRef;
	children: IContextChildren<SearchRulesProviderChildrenProps>;
};

const SearchRulesProviderInner = (
	{children, ...props}: SearchRulesProviderProps,
	ref: SearchRulesProviderRef,
) => {
	const searchRules = useSearchRules(props);

	useImperativeHandle(ref, () => searchRules, [searchRules]);

	return (
		<SearchRulesContext.Provider value={searchRules}>
			{renderChildrenOrChildrenFunction(children, searchRules)}
		</SearchRulesContext.Provider>
	);
};

export const SearchRulesProviderPrimitive = React.forwardRef<
	SearchRulesContextType,
	SearchRulesProviderProps
>(SearchRulesProviderInner);

/**
 * search rules context provider\
 * used for setting rules to execute a search (not for managing/editing)
 * @example
 * <SearchRulesProvider ref={searchRulesRef}>
 * 	...
 * </SearchRulesProvider>
 * @example
 * <SearchRulesProvider ref={searchRulesRef}>
 * 	{(searchRules) => (
 * 		...
 * 	)}
 * </SearchRulesProvider>
 */
export const SearchRulesProvider = React.memo(SearchRulesProviderPrimitive);

export default SearchRulesProvider;
